import { z } from 'zod';
import { CountriesEnum } from './countries';

// The keys are STRING + a Zod union, NEVER a Postgres ENUM: adding a section
// tomorrow must be a code change, not a migration (ALTER TYPE).
export const PUBLIC_PAGE_SECTION_KEYS = [
  'about',
  'locations',
  'contact',
  'social',
  'pricing',
  'content',
  'hours',
  'trainers',
  'businessModel',
] as const;
export type PublicPageSectionKey = (typeof PUBLIC_PAGE_SECTION_KEYS)[number];

// `trainers` exists only on an org page — a trainer's own page has no team.
// `businessModel` is the org's Terms of Use answers (Business Model, Payment
// Policy) — an organization's document, never a trainer's.
export const ORG_ONLY_SECTION_KEYS: readonly PublicPageSectionKey[] = [
  'trainers',
  'businessModel',
];

export const SERVICE_KEYS = ['LIVE', 'ONLINE', 'HYBRID', 'NUTRITION'] as const;
export type ServiceKey = (typeof SERVICE_KEYS)[number];

// PHONE was added in the final review round: the design (section→source
// table) calls for "1..n phones, emails, websites", and phase F0 added
// Contacts.isPrimary precisely so a second phone row is safe.
// The kind is a STRING column (contact.channel.entity.ts), so a new kind is
// NOT a migration.
export const CONTACT_CHANNEL_KINDS = ['EMAIL', 'PHONE', 'WEBSITE'] as const;
export type ContactChannelKind = (typeof CONTACT_CHANNEL_KINDS)[number];

// `ContactChannels.purpose` — two lists in one table that never mix.
// 'GENERAL' is the public contact list (every route in this file); 'SALES' is
// the organization's one sales contact person, written only by the profile's
// Business Details section. A STRING column, like `kind`.
export const CONTACT_CHANNEL_PURPOSES = ['GENERAL', 'SALES'] as const;
export type ContactChannelPurpose = (typeof CONTACT_CHANNEL_PURPOSES)[number];

// Deliberately PERMISSIVE: phone formats differ from country to country
// (+381 11 123-4567, 011/123-4567, (011) 123 4567 …) and the value here is a
// display string for a `tel:` link, not something we dial programmatically.
// We only require that it starts with a digit or "+", contains nothing but
// characters that occur in phone numbers, and has at least 5 digits —
// anything stricter would reject valid numbers from countries we didn't
// think of.
const PHONE_SHAPE = /^[+\d][\d\s()./+-]*$/;
const PHONE_MIN_DIGITS = 5;
export function isPlausiblePhoneNumber(value: string) {
  return (
    PHONE_SHAPE.test(value) &&
    (value.match(/\d/g)?.length ?? 0) >= PHONE_MIN_DIGITS
  );
}

// `z.string().url()` accepts ANY WHATWG-valid URL — `javascript:alert(1)` and
// `data:text/html,...` included. These values render as raw `href`s on the
// ANONYMOUS public page (ContactSection/SocialSection), so the scheme is
// pinned to http(s) at the write boundary; the FE additionally refuses to
// link a value that fails this same check (defense in depth, one shared
// implementation).
const HTTP_URL_SHAPE = /^https?:\/\//i;
export function isHttpUrl(value: string) {
  return (
    HTTP_URL_SHAPE.test(value) && z.string().url().safeParse(value).success
  );
}

// Fix round 1/5, Finding 2. STRING, not a Postgres ENUM — `SocialMedia.type`
// was migrated ENUM→VARCHAR specifically because the old enum had no
// YOUTUBE and every new network would need `ALTER TYPE` (design doc §
// "Društvene mreže"). Includes the legacy 5 (`socialMedia.mappers.ts`'s
// `DEFAULT_SOCIAL_MEDIA`) plus YOUTUBE, the type that migration was done to
// allow without a schema change.
export const PUBLIC_PAGE_SOCIAL_TYPES = [
  'FACEBOOK',
  'INSTAGRAM',
  'LINKEDIN',
  'TIKTOK',
  'TWITTER',
  'YOUTUBE',
] as const;
export type PublicPageSocialType = (typeof PUBLIC_PAGE_SOCIAL_TYPES)[number];

export const PUBLIC_PAGE_CURRENCIES = ['USD', 'EUR', 'RSD', 'BAM'] as const;
export const PUBLIC_PAGE_LOCALES = ['en-US', 'sr-Latn'] as const;

// The URL is nested (/p/<org>/<trainer>), so every sub-resource under
// /p/<org>/… shares a namespace with the trainer slug. Every new sub-route
// belongs HERE.
export const RESERVED_SLUGS = [
  'content',
  'admin',
  'api',
  'auth',
  'p',
  'public',
  'public-site',
  'assets',
  'static',
  'new',
  'edit',
] as const;

const slug = z
  .string()
  .min(3, 'VALIDATION__Min3CharLimit')
  .max(40, 'VALIDATION__Max40CharLimit')
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'VALIDATION__InvalidSlug')
  .refine(
    (value) => !(RESERVED_SLUGS as readonly string[]).includes(value),
    'VALIDATION__ReservedSlug',
  );

export const PublicPageSlugDto = z.object({ slug }).strict();
export type ApiPublicPageSlugDto = z.infer<typeof PublicPageSlugDto>;

export const UpsertPublicPageDto = z
  .object({
    isEnabled: z.boolean().optional(),
    slug: slug.optional(),
    locale: z.enum(PUBLIC_PAGE_LOCALES).optional(),
    headline: z.string().max(120).nullish(),
    headlineTranslations: z
      .object({
        'en-US': z.string().max(120).nullish(),
        'sr-Latn': z.string().max(120).nullish(),
      })
      .strict()
      .nullish(),
    intro: z.string().max(2000).nullish(),
  })
  .strict();
export type ApiUpsertPublicPageDto = z.infer<typeof UpsertPublicPageDto>;

// F6 (Task 19): platform takedown switch. Deliberately its OWN dto, never
// merged into `UpsertPublicPageDto` — `isBlocked` must stay absent from the
// owner's own PUT (that dto's `.strict()` then rejects it outright, project
// CLAUDE.md: a restriction on a platform user's counterpart action here is
// the owner NEVER being able to lift a takedown, not a value that's silently
// dropped).
export const BlockPublicPageDto = z.object({ isBlocked: z.boolean() }).strict();
export type ApiBlockPublicPageDto = z.infer<typeof BlockPublicPageDto>;

// Working hours have no table — they live in the `settings` JSONB of the
// `hours` section. 0 = Sunday, 6 = Saturday (same convention as JS
// Date#getDay).
export const WorkingHoursSettingsDto = z
  .object({
    days: z
      .array(
        z.object({
          day: z.number().int().min(0).max(6),
          closed: z.boolean(),
          from: z
            .string()
            .regex(/^\d{2}:\d{2}$/)
            .nullish(),
          to: z
            .string()
            .regex(/^\d{2}:\d{2}$/)
            .nullish(),
        }),
      )
      .max(7),
    note: z.string().max(200).nullish(),
  })
  .strict();
export type ApiWorkingHoursSettingsDto = z.infer<
  typeof WorkingHoursSettingsDto
>;

export const UpsertPublicPageSectionDto = z
  .object({
    key: z.enum(PUBLIC_PAGE_SECTION_KEYS),
    isVisible: z.boolean(),
    sortOrder: z.number().int().min(0).max(100),
    settings: z.record(z.unknown()).nullish(),
  })
  .strict();
export type ApiUpsertPublicPageSectionDto = z.infer<
  typeof UpsertPublicPageSectionDto
>;

export const UpsertContactChannelDto = z
  .object({
    kind: z.enum(CONTACT_CHANNEL_KINDS),
    value: z.string().min(3).max(255),
    label: z.string().max(60).nullish(),
    isPublic: z.boolean(),
    sortOrder: z.number().int().min(0).max(100).optional(),
  })
  .strict()
  .superRefine((channel, ctx) => {
    const check: Record<ContactChannelKind, { ok: boolean; message: string }> =
      {
        EMAIL: {
          ok: z.string().trim().email().safeParse(channel.value).success,
          message: 'VALIDATION__InvalidEmail',
        },
        PHONE: {
          ok: isPlausiblePhoneNumber(channel.value),
          message: 'VALIDATION__InvalidPhoneNumber',
        },
        WEBSITE: {
          ok: isHttpUrl(channel.value),
          message: 'VALIDATION__InvalidUrl',
        },
      };
    const { ok, message } = check[channel.kind];
    if (!ok) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['value'] });
    }
  });
export type ApiUpsertContactChannelDto = z.infer<
  typeof UpsertContactChannelDto
>;

export const UpsertServiceOfferingDto = z
  .object({
    // Legacy fixed kinds; a Profile v2 pricing item names itself with `title`
    // instead. At least one of the two (DB CHECK
    // `service_offerings_title_or_service_key`, refined below).
    serviceKey: z.enum(SERVICE_KEYS).nullish(),
    title: z.string().trim().min(1).max(120).nullish(),
    description: z.string().max(1000).nullish(),
    priceFrom: z.number().min(0).nullish(),
    priceTo: z.number().min(0).nullish(),
    // O-7: stored, not derived; the BE suggests a value, the owner may change it
    currency: z.enum(PUBLIC_PAGE_CURRENCIES),
    unit: z.string().max(40).nullish(),
    note: z.string().max(200).nullish(),
    isPublic: z.boolean(),
    sortOrder: z.number().int().min(0).max(100).optional(),
  })
  .strict()
  .refine((offering) => Boolean(offering.serviceKey || offering.title), {
    message: 'VALIDATION__Required',
    path: ['title'],
  })
  .refine(
    (offering) =>
      !isFiniteNumber(offering.priceFrom) ||
      !isFiniteNumber(offering.priceTo) ||
      offering.priceTo >= offering.priceFrom,
    { message: 'VALIDATION__PriceRangeReversed', path: ['priceTo'] },
  );
export type ApiUpsertServiceOfferingDto = z.infer<
  typeof UpsertServiceOfferingDto
>;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// `locations` section (Task 15) — no new table: `Address` is already
// `HasMany` and XOR (userId/organizationId), with `label`/`sortOrder`/
// `isAddressPublic` already added (F0 migration). All address fields are
// deliberately nullish — the design doc says "1..n locations, address
// optional": a row may be just a name (label) with no address field at all.
export const UpsertPublicPageLocationDto = z
  .object({
    label: z.string().max(60).nullish(),
    street: z.string().max(255).nullish(),
    city: z.string().max(255).nullish(),
    state: z.string().max(255).nullish(),
    country: CountriesEnum.nullish(),
    zip: z.string().max(60).nullish(),
    isAddressPublic: z.boolean(),
    sortOrder: z.number().int().min(0).max(100).optional(),
  })
  .strict();
export type ApiUpsertPublicPageLocationDto = z.infer<
  typeof UpsertPublicPageLocationDto
>;

// `social` section (fix round 1/5, Finding 2) — no new table: `SocialMedia`
// is already `HasMany` and XOR (userId/organizationId), with
// `isPublic`/`sortOrder` added in the same migration as Address
// (F0/public-page).
export const UpsertPublicPageSocialLinkDto = z
  .object({
    type: z.enum(PUBLIC_PAGE_SOCIAL_TYPES),
    // http(s) only, not just any URL — see `isHttpUrl`'s doc.
    url: z.string().max(255).refine(isHttpUrl, 'VALIDATION__InvalidUrl'),
    isPublic: z.boolean(),
    sortOrder: z.number().int().min(0).max(100).optional(),
  })
  .strict();
export type ApiUpsertPublicPageSocialLinkDto = z.infer<
  typeof UpsertPublicPageSocialLinkDto
>;
